'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import i18n from '@/shared/i18n';

// Synchronously set language BEFORE React renders
if (typeof window !== 'undefined') {
  const pathname = window.location.pathname;
  let initialLang = 'en';

  // Check Russian routes FIRST (before English catch-all)
  if (pathname === '/ru' || pathname.startsWith('/ru/') || pathname === '/posts/ru' || pathname.startsWith('/posts/ru/')) {
    initialLang = 'ru';
  } else if (pathname === '/' || pathname.startsWith('/practice') || pathname.startsWith('/posts') || pathname.startsWith('/about')) {
    initialLang = 'en';
  }

  // Set language immediately if it's not already correct
  if (i18n.language !== initialLang) {
    i18n.changeLanguage(initialLang);
  }
  document.documentElement.lang = initialLang;

  // No cookie needed for practice routes - they use file-based routing
  // (/practice = en, /ru/practice = ru)
  // Only /account page uses language cookie (account-lang)
}

export default function I18nProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    // Initialize i18n on the client side
    if (typeof window !== 'undefined') {
      window.i18n = i18n;

      // Listen for i18next language changes and update HTML lang attribute
      const handleLanguageChange = (lng: string) => {
        document.documentElement.lang = lng;
      };

      i18n.on('languageChanged', handleLanguageChange);

      return () => {
        i18n.off('languageChanged', handleLanguageChange);
      };
    }
  }, []);

  useEffect(() => {
    // Sync language with URL path on route changes (for client-side navigation)
    if (typeof window !== 'undefined') {
      // Check for Russian routes FIRST (before English catch-all)
      if (pathname === '/ru' || pathname.startsWith('/ru/') || pathname === '/posts/ru' || pathname.startsWith('/posts/ru/')) {
        // All /ru routes should be Russian
        const detectedLang = 'ru';

        // Update HTML lang attribute
        document.documentElement.lang = detectedLang;

        // Update i18next only if different
        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }
      } else if (pathname === '/' || pathname === '/about' || pathname.startsWith('/posts') || pathname.startsWith('/practice')) {
        // All these routes should be English (after Russian routes are excluded)
        const detectedLang = 'en';

        // Update HTML lang attribute
        document.documentElement.lang = detectedLang;

        // Update i18next only if different (avoid unnecessary change events)
        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }
      } else {
        // For other routes, sync HTML lang with current i18next language
        // This ensures the HTML lang matches the cookie-based language from middleware
        const currentLang = i18n.language || document.documentElement.lang;
        if (document.documentElement.lang !== currentLang) {
          document.documentElement.lang = currentLang;
        }
      }
    }
  }, [pathname]);

  return <>{children}</>;
} 